/**
 * Shared types + pure helpers for recommendation geocoding.
 *
 * The pipeline (see GeocodeRecommendationQueue) resolves coordinates from
 * Google's APIs only — the LLM classifier never emits coordinates or place
 * IDs, just text (an extracted venue name + locality) and a specificity
 * verdict. These helpers validate geocoder results so that a bad text query
 * yields "no pin" rather than a wrong pin.
 */

export const GEO_PRECISIONS = [
  "venue",
  "neighborhood",
  "city",
  "region",
  "country",
] as const;
export type GeoPrecision = (typeof GEO_PRECISIONS)[number];

export const GEO_SOURCES = ["maps_link", "pipeline", "manual"] as const;
export type GeoSource = (typeof GEO_SOURCES)[number];

export const GEO_SPECIFICITIES = ["venue", "area", "origin_only"] as const;
export type GeoSpecificity = (typeof GEO_SPECIFICITIES)[number];

// Null-ish placeholders LLMs emit instead of omitting a field. Bare "NA" is
// deliberately absent: it's the ISO-2 code for Namibia (real prod data).
const JUNK_GEO_VALUES = new Set([
  "null",
  "none",
  "n/a",
  "unknown",
  "nil",
  "undefined",
]);

// Whitespace plus straight/smart quotes and backticks, stripped from both
// ends before the junk check ('"null"' is as junk as 'null').
const EDGE_QUOTES = /^[\s'"`‘’“”]+|[\s'"`‘’“”]+$/g;

/**
 * The classifier occasionally emits junk placeholders (the literal string
 * "null", sometimes quoted, has shown up in prod geo columns). Collapse
 * those to real nulls — a matching one-time DB scrub lives in migration
 * 20260710155736 — and strip stray surrounding quotes from real values.
 */
export function sanitizeGeoText(
  raw: string | null | undefined,
): string | null {
  if (raw == null) return null;
  const trimmed = raw.replace(EDGE_QUOTES, "");
  if (!trimmed) return null;
  if (JUNK_GEO_VALUES.has(trimmed.toLowerCase())) return null;
  return trimmed;
}

// Common non-ISO country values observed in prod geo_country ("USA", "UK",
// "Denmark", "Türkiye"...). Not exhaustive — the classifier is instructed to
// emit ISO-2; this catches the frequent strays.
const COUNTRY_ALIASES: Record<string, string> = {
  usa: "US",
  "united states": "US",
  "united states of america": "US",
  uk: "GB",
  "united kingdom": "GB",
  england: "GB",
  scotland: "GB",
  wales: "GB",
  denmark: "DK",
  france: "FR",
  germany: "DE",
  italy: "IT",
  spain: "ES",
  portugal: "PT",
  netherlands: "NL",
  belgium: "BE",
  switzerland: "CH",
  austria: "AT",
  sweden: "SE",
  norway: "NO",
  finland: "FI",
  ireland: "IE",
  greece: "GR",
  turkey: "TR",
  "türkiye": "TR",
  japan: "JP",
  china: "CN",
  "south korea": "KR",
  india: "IN",
  thailand: "TH",
  vietnam: "VN",
  indonesia: "ID",
  philippines: "PH",
  singapore: "SG",
  australia: "AU",
  "new zealand": "NZ",
  canada: "CA",
  mexico: "MX",
  brazil: "BR",
  argentina: "AR",
  chile: "CL",
  peru: "PE",
  colombia: "CO",
  "south africa": "ZA",
  morocco: "MA",
  egypt: "EG",
  israel: "IL",
  "united arab emirates": "AE",
  "czech republic": "CZ",
  czechia: "CZ",
  poland: "PL",
  hungary: "HU",
  croatia: "HR",
  iceland: "IS",
};

export function normalizeCountryCode(
  raw: string | null | undefined,
): string | null {
  const cleaned = sanitizeGeoText(raw);
  if (!cleaned) return null;
  // Alias lookup first: "UK" is two letters but the ISO code is GB.
  const aliased = COUNTRY_ALIASES[cleaned.toLowerCase()];
  if (aliased) return aliased;
  if (/^[A-Za-z]{2}$/.test(cleaned)) return cleaned.toUpperCase();
  return cleaned;
}

/** Lowercase, strip diacritics and punctuation, collapse whitespace. */
export function normalizeForMatch(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Fraction of `expected`'s tokens that appear in `actual` (order-free).
 * Used to compare the extracted venue name against what the geocoder
 * returned: "Principe di Firenze" vs "Monumento all'Indiano" → 0.
 */
export function nameSimilarity(expected: string, actual: string): number {
  const expectedTokens = normalizeForMatch(expected).split(" ").filter(Boolean);
  if (expectedTokens.length === 0) return 0;
  const actualTokens = normalizeForMatch(actual).split(" ").filter(Boolean);
  const actualSet = new Set(actualTokens);
  // Long tokens also match by shared prefix ("kallbadhus" ~ "kallbadhuset")
  // but only prefix — substring matching would let "market" claim
  // "supermarket", a false-positive path for wrong venues.
  const matched = expectedTokens.filter(
    (t) =>
      actualSet.has(t) ||
      (t.length >= 5 &&
        actualTokens.some(
          (a) => a.length >= 5 && (a.startsWith(t) || t.startsWith(a)),
        )),
  );
  return matched.length / expectedTokens.length;
}

/**
 * Google result types that indicate a geopolitical/administrative area rather
 * than an actual establishment. A venue query that resolves to only these is
 * a miss (the geocoder fell back to the city), not a venue hit.
 */
const GEOPOLITICAL_TYPES = new Set([
  "political",
  "locality",
  "sublocality",
  "sublocality_level_1",
  "sublocality_level_2",
  "neighborhood",
  "administrative_area_level_1",
  "administrative_area_level_2",
  "administrative_area_level_3",
  "administrative_area_level_4",
  "country",
  "postal_code",
  "postal_town",
  "colloquial_area",
  "geocode",
  "route",
  "street_address",
  "premise",
  "plus_code",
]);

export interface VenueCandidate {
  name?: string;
  formatted_address?: string;
  types?: string[];
}

export interface VenueVerdict {
  accept: boolean;
  reason: string;
  nameScore: number;
}

/**
 * Sanity-check a Places candidate against the extracted venue name +
 * locality. Rules (each earned by a failure in the 2026-07-09 dry run):
 * 1. Reject results whose types are all geopolitical (city fallback).
 * 2. Reject weak name matches (< 0.35) outright — a wrong venue is worse
 *    than no venue.
 * 3. Accept strong name matches (>= 0.6). In between, require the address
 *    to corroborate the expected locality. Locality corroboration is a
 *    soft signal only — formatted addresses use endonyms ("København")
 *    while the classifier may emit exonyms ("Copenhagen"), so we never
 *    reject a strong name match on locality alone.
 */
export function evaluateVenueCandidate(
  placeName: string,
  locality: { city?: string | null; state?: string | null },
  candidate: VenueCandidate,
): VenueVerdict {
  const types = candidate.types ?? [];
  if (types.length > 0 && types.every((t) => GEOPOLITICAL_TYPES.has(t))) {
    return {
      accept: false,
      reason: "geopolitical_result",
      nameScore: 0,
    };
  }

  const nameScore = candidate.name
    ? nameSimilarity(placeName, candidate.name)
    : 0;
  if (nameScore >= 0.6) {
    return { accept: true, reason: "strong_name_match", nameScore };
  }
  if (nameScore < 0.35) {
    return { accept: false, reason: "weak_name_match", nameScore };
  }

  const address = normalizeForMatch(candidate.formatted_address ?? "");
  const localityParts = [locality.city, locality.state]
    .map((p) => sanitizeGeoText(p))
    .filter((p): p is string => !!p)
    .map(normalizeForMatch);
  const corroborated =
    localityParts.length > 0 && localityParts.some((p) => address.includes(p));
  return corroborated
    ? { accept: true, reason: "name_and_locality_match", nameScore }
    : { accept: false, reason: "uncorroborated_partial_match", nameScore };
}

/**
 * Map Geocoding API result types to our precision tiers (area path). Order
 * matters: prefer the most specific tier present.
 */
export function precisionFromGeocodeTypes(types: string[]): GeoPrecision {
  const typeSet = new Set(types);
  if (typeSet.has("neighborhood") || typeSet.has("sublocality") ||
      typeSet.has("sublocality_level_1")) {
    return "neighborhood";
  }
  if (typeSet.has("locality") || typeSet.has("postal_town")) return "city";
  if (typeSet.has("country")) return "country";
  if (
    [...typeSet].some((t) => t.startsWith("administrative_area_level")) ||
    typeSet.has("colloquial_area")
  ) {
    return "region";
  }
  // Parks, landmarks etc. can come back from the area path (e.g. "Central
  // Park, New York") — treat anything more specific than a city as
  // neighborhood-level rather than a venue pin.
  return "neighborhood";
}

/**
 * Canonical display components extracted from Google address components.
 * These are the "final, good" versions of the geo text (canonical
 * spellings, tied to the resolved pin) — the classifier's free-text geo
 * hints are never shown to users.
 */
export interface CanonicalGeo {
  neighborhood: string | null;
  locality: string | null;
  /** Short admin-area code, e.g. "CA", "BC", "NSW". */
  regionCode: string | null;
  /** ISO 3166-1 alpha-2, e.g. "US", "DK". */
  countryCode: string | null;
}

export interface GoogleAddressComponent {
  long_name?: string;
  short_name?: string;
  types?: string[];
}

function componentOfType(
  components: GoogleAddressComponent[],
  types: string[],
  field: "long_name" | "short_name",
): string | null {
  for (const wanted of types) {
    const match = components.find((c) => c.types?.includes(wanted));
    if (match?.[field]) return match[field] ?? null;
  }
  return null;
}

/**
 * Parse a Google `address_components` array into canonical display parts.
 * Locality falls back through postal_town (UK) and sublocality (e.g. some
 * Brooklyn/Tokyo addresses have no locality component).
 */
export function extractCanonicalComponents(
  components: GoogleAddressComponent[] | undefined,
): CanonicalGeo {
  const list = components ?? [];
  return {
    neighborhood: componentOfType(
      list,
      ["neighborhood", "sublocality_level_2"],
      "long_name",
    ),
    locality: componentOfType(
      list,
      ["locality", "postal_town", "sublocality_level_1", "sublocality"],
      "long_name",
    ),
    regionCode: componentOfType(
      list,
      ["administrative_area_level_1"],
      "short_name",
    ),
    countryCode: componentOfType(list, ["country"], "short_name"),
  };
}

// Countries where "Locality, REGION" is the conventional label ("Venice,
// CA"). Everywhere else the region code is unfamiliar — use the country.
const REGION_LABEL_COUNTRIES = new Set(["US", "CA", "AU"]);

const countryNames = new Intl.DisplayNames(["en"], { type: "region" });

/**
 * English country name for geocoder QUERY text. Google's Geocoding/Places
 * APIs don't treat bare ISO codes as addresses — "Skógafoss Waterfall, IS"
 * and country-only areas like "CL" fail outright (11 of the first 16 prod
 * backfill failures). Storage keeps ISO codes; queries get names.
 */
export function countryNameForQuery(
  raw: string | null | undefined,
): string | null {
  const code = normalizeCountryCode(raw);
  if (!code) return null;
  if (!/^[A-Z]{2}$/.test(code)) return code;
  try {
    return countryNames.of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * The v1 rec-card label: "Venice, CA" / "Vancouver, BC" /
 * "Copenhagen, Denmark". Pure and viewer-independent so it works in SSR,
 * OG images, and emails; clients MAY sharpen it locally (e.g. drop the
 * region when the viewer is nearby) since cards carry the raw components.
 */
export function formatCanonicalGeoLabel(geo: {
  geo_canonical_locality?: string | null;
  geo_canonical_region_code?: string | null;
  geo_canonical_country_code?: string | null;
}): string | null {
  const locality = geo.geo_canonical_locality;
  const region = geo.geo_canonical_region_code;
  const country = geo.geo_canonical_country_code;

  if (country && REGION_LABEL_COUNTRIES.has(country)) {
    if (locality && region) return `${locality}, ${region}`;
    if (locality || region) return locality || region || null;
  }

  let countryName: string | null = null;
  if (country) {
    try {
      countryName = countryNames.of(country) ?? country;
    } catch {
      countryName = country;
    }
  }
  if (locality && countryName) return `${locality}, ${countryName}`;
  return locality ?? countryName ?? null;
}

/**
 * A rec's maps link if it's renderable, else null. maps_location is
 * creator-entered, so only a plain http(s) URL counts — never a
 * javascript:/data: scheme. Surfaces key their pin-row layout on this, so it
 * must match exactly what RecommendationLocationLink will render.
 */
export function getValidatedMapsLocation(
  mapsLocation: string | null | undefined,
): string | null {
  return mapsLocation && /^https?:\/\//i.test(mapsLocation)
    ? mapsLocation
    : null;
}

/** A Google Maps text search that does not imply a precise map pin. */
export function googleMapsSearchUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

/**
 * A canonical Google Maps URL for a resolved place. Written into
 * maps_location for pipeline-resolved venues so shipped clients (which gate
 * pins and "Open in Maps" on maps_location) work without a release.
 */
export function buildSyntheticMapsUrl(
  latitude: number,
  longitude: number,
  placeId: string | null,
): string {
  const query = `${latitude},${longitude}`;
  const placeParam = placeId
    ? `&query_place_id=${encodeURIComponent(placeId)}`
    : "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}${placeParam}`;
}

const SYNTHETIC_MAPS_URL_PREFIX =
  "https://www.google.com/maps/search/?api=1&query=";

/** True when the URL was generated from the rec's stored geo coordinates. */
export function isSyntheticMapsUrl(url: string): boolean {
  return url.startsWith(SYNTHETIC_MAPS_URL_PREFIX);
}

/** "Talat Market" + {city: "Atlanta", state: "GA", country: "US"} → search query. */
export function buildVenueSearchQuery(
  placeName: string,
  locality: {
    neighborhood?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
  },
): string {
  const parts = [
    placeName,
    sanitizeGeoText(locality.neighborhood),
    sanitizeGeoText(locality.city),
    sanitizeGeoText(locality.state),
    countryNameForQuery(locality.country),
  ].filter(Boolean);
  return parts.join(", ");
}
